import {
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Checkbox,
  FormLabel,
  Divider,
  Alert,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Add, Edit } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";

import { useGetJeopardyStakeholders } from "../../../shared/hooks";
import { AutoComplete, DataLayout, FieldControl } from "../../../shared/components";
import { useFetchApproval } from "../../stageGates/hooks/useFetchApprovals";
import { useFetchChecklist } from "../../stageGates/hooks/useFetchChecklist";
import { ProjectArtefact } from "../../artefacts/component/ProjectArtefact";
import { useStageGate } from "../../projectOverview/hooks/useStageGate";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { useGetSchedule } from "../mutations";
import usePhaseData from "../hooks/usePhaseData";
import { useProject } from "../../projectOverview/hooks/useProject";
import { useFilterContext } from "../../../shared/context";

export interface StageGateFormHandle {
  submit: () => void;
}

interface StageGateFormProps {
  onSave: (data: StageGateFormData) => void;
  onCancel: () => void;
  initialData?: Partial<StageGateFormData>;
  stageGateId: number | null;
  isEditing?: boolean;
  phaseOptions?: Array<{ id: number; name: string }>;
  projectId: string | undefined;
  phaseData?: any;
  stageGateDisabledConfig: Record<string, boolean>;
  isStageGateCompleted: boolean;

}

export interface StageGateFormData {
  title: string;
  description: string;
  owner: string;
  userId?: number;
  plannedStartDate: Dayjs | null;
  plannedEndDate: Dayjs | null;
  actualEndDate?: Dayjs;
  parentPhaseId?: number;
  parentPhase?: string;
  stage_gate_id?: number;
}

export const StageGateForm = forwardRef<
  StageGateFormHandle,
  StageGateFormProps
>(function StageGateForm(
  {
    onSave,
    onCancel,
    initialData,
    stageGateId,
    isEditing,
    phaseOptions = [],
    projectId,
    phaseData,
    stageGateDisabledConfig,
    isStageGateCompleted,
  },
  ref,
) {
  const {
    isAllowedAddApprover,
    isAllowedEditApprover,
    isAllowedAddChecklist,
    isAllowedEditChecklist,
    isAllowedAddGateDecisionOwner
  } = useCapabilities();

  const [currentTab, setCurrentTab] = useState(0);
  const { projectData } = useProject(projectId);


  const { data: stakeholders, isLoading: isLoadingUsers } = useGetJeopardyStakeholders({ params: { project_id: projectId } });
  const { options: {"stage-gates" : stageGateOptions }} = useFilterContext();
  const userOptions = useMemo(() => {
    return (
      stakeholders?.data?.flatMap((role: any) =>
        role?.users?.map((user: any) => ({
          role: role.role_name,
          name: user.user_name,
          phone: user.phone || "N/A",
          email: user.email,
          id: user.user_id,
        })),
      ) || []
    );
  }, [stakeholders, isLoadingUsers]);

  const { removeCache: removeStageGate } = useStageGate(projectId);
  const { removeCache: removeSchedule } = useGetSchedule(Number(stageGateId));

  const [formStatusApprover, setFormStatusApprover] = useState(false);

  const [approvalsPage, setApprovalsPage] = useState(1);
  const [accumulatedApprovals, setAccumulatedApprovals] = useState<any[]>([]);

  const {
    data: approvalsData,
    pagination: approvalsPagination,
    handleAssignApprover,
    handleUpdateApprover,
    isPending: isFetchingApprover,
    isAdding: isAddingApprover,
    isEditing: isEditingApprover,
    isUpdating: isUpdatingApprover,
  } = useFetchApproval({ stageID: String(stageGateId), projectId: projectId!, page: approvalsPage });

  const [formStatusChecklist, setFormStatusChecklist] = useState(false);

  useEffect(() => {
      setApprovalsPage(1);
      setAccumulatedApprovals([]);
  }, [stageGateId]);

  useEffect(() => {
      if (approvalsData && Array.isArray(approvalsData)) {
          setAccumulatedApprovals(prev => {
              if (approvalsPage === 1) {
                  if (prev.length === 0 && approvalsData.length === 0) return prev;
                  if (prev === approvalsData) return prev;
                  return approvalsData;
              }
              const newItems = approvalsData.filter((newItem: any) => !prev.some((oldItem: any) => oldItem.id === newItem.id));
              if (newItems.length === 0) return prev;
              return [...prev, ...newItems];
          });
      }
  }, [approvalsData, approvalsPage]);

  const [checklistsPage, setChecklistsPage] = useState(1);
  const [accumulatedChecklists, setAccumulatedChecklists] = useState<any[]>([]);

  const {
    data: checklistsData,
    pagination: checklistsPagination,
    handleAddChecklist,
    handleEditChecklist,
    isPending: isFetchingCheckList,
    isAdding: isAddingChecklist,
    isEditing: isEditingChecklist,
  } = useFetchChecklist(String(stageGateId), checklistsPage);

  useEffect(() => {
      setChecklistsPage(1);
      setAccumulatedChecklists([]);
  }, [stageGateId]);

  useEffect(() => {
      if (checklistsData && Array.isArray(checklistsData)) {
          setAccumulatedChecklists(prev => {
              if (checklistsPage === 1) {
                  if (prev.length === 0 && checklistsData.length === 0) return prev;
                  if (prev === checklistsData) return prev;
                  return checklistsData;
              }
              const newItems = checklistsData.filter((newItem: any) => !prev.some((oldItem: any) => oldItem.task_id === newItem.task_id));
              if (newItems.length === 0) return prev;
              return [...prev, ...newItems];
          });
      }
  }, [checklistsData, checklistsPage]);

  const approvalsScrollTimeout = useRef<number | null>(null);

  const handleApprovalsScroll = (e: any) => {
      const target = e.currentTarget;
      if(approvalsScrollTimeout.current) clearTimeout(approvalsScrollTimeout.current);
      approvalsScrollTimeout.current = window.setTimeout(() => {
          if (!target) return;
          const threshold = 50;
          const atBottom = target.scrollHeight - target.scrollTop - target.clientHeight < threshold;
          if (atBottom && approvalsPagination) {
              if (approvalsPage * approvalsPagination.limit < approvalsPagination.total && !isFetchingApprover) {
                  setApprovalsPage(prev => prev + 1);
              }
          }
      }, 100);
  };

  const checklistsScrollTimeout = useRef<number | null>(null);

  const handleChecklistsScroll = (e: any) => {
      const target = e.currentTarget;
      if(checklistsScrollTimeout.current) clearTimeout(checklistsScrollTimeout.current);
      checklistsScrollTimeout.current = window.setTimeout(() => {
          if (!target) return;
          const threshold = 50;
          const atBottom = target.scrollHeight - target.scrollTop - target.clientHeight < threshold;
          if (atBottom && checklistsPagination) {
              if (checklistsPage * checklistsPagination.limit < checklistsPagination.total && !isFetchingCheckList) {
                  setChecklistsPage(prev => prev + 1);
              }
          }
      }, 100);
  };

  const approvalsScrollRef = useRef<HTMLDivElement>(null);
  const checklistsScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (formStatusApprover && approvalsScrollRef.current) {
      approvalsScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [formStatusApprover]);

  useEffect(() => {
    if (formStatusChecklist && checklistsScrollRef.current) {
      checklistsScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [formStatusChecklist]);

  /** ================= FORM DATA ================= */

  const defaultFormData: StageGateFormData = {
    title: "",
    description: "",
    owner: "",
    userId: undefined,
    plannedStartDate: null,
    plannedEndDate: null,
    actualEndDate: undefined,
    parentPhaseId: undefined,
    parentPhase: "",
  };

  const [formData, setFormData] = useState<StageGateFormData>({
    ...defaultFormData,
    ...initialData,
  });

  /** ================= PARENT PHASE DATA ================= */

  const { parentPhase } = usePhaseData({
    projectId: Number(projectId),
    phaseId: Number(formData?.parentPhaseId),
  });

  const phaseStartDate = useMemo(() => parentPhase?.planned_start_date ? dayjs(parentPhase.planned_start_date).startOf('day') : null, [parentPhase]);
  const phaseEndDate = useMemo(() => parentPhase?.planned_end_date ? dayjs(parentPhase.planned_end_date).startOf('day') : null, [parentPhase]);

  /** ================= APPROVAL STATE ================= */
  const [approver, setApprover] = useState<{
    id?: number;
    isEditing?: boolean;
    status: boolean;
    userId: number | null;
    isGateDecision: boolean;
  }>({
    status: false,
    userId: null,
    isGateDecision: false,
    isEditing: false,
  });

  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [isEditApprover, setIsEditApprover] = useState(false);

  const filteredOptions = useMemo(() => {
    const selectedUserIds = new Set(
      accumulatedApprovals?.map((a: any) => a.user_id) || [],
    );

    return userOptions
      .filter((u) => {
        // Exclude users already assigned, unless editing the same user
        return (
          !selectedUserIds.has(u.id) ||
          (isEditApprover && approver.userId === u.id)
        );
      })
      .map((u) => ({
        value: u.id,
        label: u.name,
      }));
  }, [userOptions, accumulatedApprovals, isEditApprover, approver.userId]);

  const isGateDecisionExist = useMemo(() => {
    return (
      accumulatedApprovals?.some((approval: any) => approval.is_decision_gate) || false
    );
  }, [accumulatedApprovals]);

  /** ================= CHECKLIST STATE ================= */
  const [checkList, setCheckList] = useState<{
    id?: number;
    isEditing?: boolean;
    task_name: string | null;
    userId: number | null;
    owner: string | null;
    isEvidenceReq: boolean;
  }>({
    task_name: null,
    userId: null,
    owner: null,
    isEvidenceReq: false,
    isEditing: false,
  });

  /** ================= HANDLERS ================= */

  const handleEditApprover = (approval: any) => {
    setIsEditApprover(true);
    setFormStatusApprover(true);
    setApprover({
      status: true,
      isEditing: true,
      userId: approval.user_id,
      isGateDecision: approval.is_decision_gate,
      id: approval.id,
    });
  };

  const endDateMinDate = useMemo(() => {
    let min = formData.plannedStartDate ? dayjs(formData.plannedStartDate).startOf('day') : (isStageGateCompleted ? dayjs().startOf('day') : undefined);
    return min;
  }, [formData.plannedStartDate, isStageGateCompleted]);

  const handleApproverSubmit = async () => {
    if (!approver.userId) return;

    // Prevent duplicate gate decision owner
    if (approver.isGateDecision && isGateDecisionExist && !approver.isEditing) {
      setValidationMessage("Only one Gate Decision Owner can be assigned.");
      return;
    }

    // Prevent assigning same user twice
    const alreadyAssigned = accumulatedApprovals?.some(
      (a: any) => a.user_id === approver.userId && a.id !== approver.id,
    );
    if (alreadyAssigned) {
      setValidationMessage("This user is already assigned a role.");
      return;
    }

    const payload = {
      user_id: approver.userId,
      is_decision_gate: approver.isGateDecision,
      work_stream_id: projectData?.workstream_id || null,
    };

    try {
      if (approver.isEditing && approver.id) {
        await handleUpdateApprover(String(approver.id), payload);
      } else {
        await handleAssignApprover(payload);
      }

      setApprovalsPage(1);
      setAccumulatedApprovals([]);

      removeStageGate();
      removeSchedule();

      // Reset state
      setApprover({
        status: false,
        userId: null,
        isGateDecision: false,
        isEditing: false,
      });
      setIsEditApprover(false);
      setFormStatusApprover(false);
      setValidationMessage(null);
    } catch (err) {
      console.error("Failed to submit approver:", err);
    }
  };

  const handleChecklistSubmit = async () => {
    if (!checkList.task_name || !checkList.userId) return;
    const payload = {
      task_name: checkList.task_name,
      user_id: checkList.userId,
      is_evidence_required: checkList.isEvidenceReq,
      is_required: false,
      object_id: stageGateId || null,
      object_type: "checklist",
      project_id: Number(projectId),
    };
    if (checkList.isEditing && checkList.id) {
      await handleEditChecklist(String(checkList.id), payload);
    } else {
      await handleAddChecklist(payload);
    }
    setChecklistsPage(1);
    setAccumulatedChecklists([]);
    
    removeStageGate();
    setFormStatusChecklist(false);
    setCheckList({
      task_name: null,
      userId: null,
      owner: null,
      isEvidenceReq: false,
      isEditing: false,
    });
  };

  const handleEditCheckList = (item: any) => {
    setFormStatusChecklist(true);
    setCheckList({
      task_name: item.task_name,
      userId: item.user_id,
      owner: item.owner,
      isEvidenceReq: item.is_evidence_required,
      id: item.task_id,
      isEditing: true,
    });
  };

  const [errors, setErrors] = useState<{
    parentPhaseId?: string;
    title?: string;
    plannedStartDate?: string;
    plannedEndDate?: string;
  }>({});

  const shouldDisablePlannedEndDate = (date: Dayjs) => {
    if (!isStageGateCompleted) return false;

    const today = dayjs().startOf("day");

    // allow current value
    if (
      initialData?.plannedEndDate &&
      date.isSame(dayjs(initialData.plannedEndDate), "day")
    ) {
      return false;
    }

    return date.isBefore(today);
  };

  const [warningQueue, setWarningQueue] = useState<string[]>([]);

  const currentWarning = warningQueue[0] ?? null;

  const handleWarningIgnore = () => {
    const remaining = warningQueue.slice(1);
    setWarningQueue(remaining);
    if (remaining.length === 0) {
      onSave(formData);
    }
  };

  const handleWarningCancel = () => {
    setWarningQueue([]);
  };

  useImperativeHandle(ref, () => ({
    submit: () => {
      const newErrors: { parentPhaseId?: string; title?: string, plannedStartDate?: string; plannedEndDate?: string } = {};
      if (!formData.parentPhaseId) {
        newErrors.parentPhaseId = "Parent Phase is required";
      }
      if (!formData.title) {
        newErrors.title = "Stage Gate is required";
      }
      if (!formData.plannedStartDate) {
        newErrors.plannedStartDate = "Planned Start Date is required";
      }
      if (!formData.plannedEndDate) {
        newErrors.plannedEndDate = "Planned End Date is required";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      const warnings: string[] = [];

      if (formData.plannedStartDate && formData.plannedEndDate) {
        const isStartBeforePhaseStart = phaseStartDate && formData.plannedStartDate.isBefore(phaseStartDate, 'day');
        const isStartAfterPhaseEnd = phaseEndDate && formData.plannedStartDate.isAfter(phaseEndDate, 'day');
        const isEndBeforePhaseStart = phaseStartDate && formData.plannedEndDate.isBefore(phaseStartDate, 'day');
        const isEndAfterPhaseEnd = phaseEndDate && formData.plannedEndDate.isAfter(phaseEndDate, 'day');

        if (isStartBeforePhaseStart || isStartAfterPhaseEnd || isEndBeforePhaseStart || isEndAfterPhaseEnd) {
          warnings.push(
            "The selected planned dates fall outside the planned dates of the parent phase. Are you sure you want to proceed?"
          );
        }
      }

      if (warnings.length > 0) {
        setWarningQueue(warnings);

      } else {
        onSave(formData);
        removeStageGate();
        removeSchedule();
      }
    }
  }));

  useEffect(() => {
    if (isEditing && initialData) {
      setFormData((prev) => ({
        ...prev,
        title: initialData.title ?? prev.title,
        description: initialData.description ?? prev.description,
        owner: initialData.owner ?? prev.owner,
        userId: initialData.userId ?? prev.userId,
        plannedStartDate:
          initialData.plannedStartDate ?? prev.plannedStartDate,
        plannedEndDate:
          initialData.plannedEndDate ?? prev.plannedEndDate,
        actualEndDate:
          initialData.actualEndDate ?? prev.actualEndDate,
        parentPhaseId: initialData.parentPhaseId ?? prev.parentPhaseId,
        parentPhase: initialData.parentPhase ?? prev.parentPhase,
        stage_gate_id: initialData.stage_gate_id ?? prev.stage_gate_id,
      }));
    } else {
      setFormData(defaultFormData);
    }
  }, [initialData]);

  /** ================= UI RENDER ================= */
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Tabs
        value={currentTab}
        onChange={(_, val) => setCurrentTab(val)}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        <Tab label="OVERVIEW" />
        <Tab label="APPROVALS" disabled={!stageGateId} />
        <Tab label="CHECKLIST" disabled={!stageGateId} />
        <Tab label="ARTEFACT" disabled={!stageGateId} />
      </Tabs>

      {initialData && isStageGateCompleted && (
        <Box px={2} mb={2}>
          <Alert severity="info">
            Cautious: The linked stage gate has been approved.
          </Alert>
        </Box>
      )}

      {currentTab === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, px: 2 }}>
          <FieldControl
            keyName={"stage_gate"}
            label={"Stage Gate"}
            variant={"select"}
            value={formData.stage_gate_id}
            options={(stageGateOptions || []).map((stageGate:{ id: number; description: string}) => ({
              value: stageGate.id,
              label: stageGate.description
            }))}
            onChange={(_, value) => {
              const selectedStageGate = (stageGateOptions || []).find((s: { id: number; description: string}) => s.id === value);
              setFormData({ ...formData, title: selectedStageGate?.description || "", stage_gate_id: selectedStageGate?.id || null});
              setErrors({ ...errors, title: undefined });
            }}
            disabled={isEditing}
            error={!!errors.title}
            helperText={errors.title}
            required
          />

          <TextField
            label="Stage Gate Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            disabled={stageGateDisabledConfig.description}
          />

          <FormControl fullWidth error={!!errors.parentPhaseId}>
            <InputLabel>Parent Phase *</InputLabel>
            <Select
              value={formData.parentPhaseId || ""}
              label="Parent Phase *"
              onChange={(e) => {
                const selectedPhase = phaseOptions.find(
                  (p) => p.id === e.target.value,
                );
                setFormData({
                  ...formData,
                  parentPhaseId: e.target.value as number,
                  parentPhase: selectedPhase?.name || "",
                });
                setErrors({ ...errors, parentPhaseId: undefined });
              }}
              disabled={stageGateDisabledConfig.parentPhaseId}
            >
              {phaseOptions.length > 0 ? (
                phaseOptions.map((phase) => (
                  <MenuItem key={phase.id} value={phase.id}>
                    {phase.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  No phases available
                </MenuItem>
              )}
            </Select>
            {errors.parentPhaseId && (
              <FormHelperText>{errors.parentPhaseId}</FormHelperText>
            )}
          </FormControl>

          <FieldControl
            keyName={"userId"}
            label={"Owner"}
            variant={"select"}
            value={formData.userId}
            options={userOptions.map((u) => ({
              value: u.id,
              label: u.name,
            }))}
            onChange={(_key: string, value: any) => {
              setFormData({
                ...formData,
                userId: value,
              });
            }}
            disabled={isLoadingUsers || stageGateDisabledConfig.owner}
          />

          <DatePicker
            label="Planned Start Date"
            format="DD/MM/YYYY"
            value={formData.plannedStartDate}
            onChange={(date) => {
              let newPlannedEndDate = formData.plannedEndDate;
              if (date && newPlannedEndDate && date.isAfter(newPlannedEndDate.startOf('day'))) {
                newPlannedEndDate = date;
              }
              setFormData({ ...formData, plannedStartDate: date, plannedEndDate: newPlannedEndDate });
              setErrors({ ...errors, plannedStartDate: undefined });
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!errors.plannedStartDate,
                helperText: errors.plannedStartDate,
              }
            }}
            disabled={stageGateDisabledConfig.plannedStartDate}

          />

          <DatePicker
            label="Planned End Date"
            format="DD/MM/YYYY"
            value={formData.plannedEndDate}
            onChange={(date) =>
              date && setFormData({ ...formData, plannedEndDate: date })
            }
            minDate={endDateMinDate}
            shouldDisableDate={shouldDisablePlannedEndDate}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!errors.plannedEndDate,
                helperText: errors.plannedEndDate,
              }
            }}
            disabled={stageGateDisabledConfig.plannedEndDate}
          />
        </Box>
      )}

      {/* APPROVALS TAB */}
      {currentTab === 1 && (
        <Box ref={approvalsScrollRef} px={2} display={"flex"} flexDirection={"column"} gap={2} sx={{ overflowY: 'auto', flex: 1 }} onScroll={handleApprovalsScroll as any}>
          {!formStatusApprover &&
            isAllowedAddApprover &&
            !isStageGateCompleted && (
              <Button
                onClick={() => setFormStatusApprover(true)}
                variant="outlined"
              >
                Create Approver
              </Button>
            )}
          {formStatusApprover && (
            <Box flexShrink={0}>
              <DataLayout title="Approvals" bgVariant="filled">
              <Box p={2}>
                <FieldControl
                  keyName={"userId"}
                  label={"Stage Owner"}
                  variant={"select"}
                  value={approver.userId}
                  options={
                    isEditApprover
                      ? userOptions.map((u) => ({
                        value: u.id,
                        label: u.name,
                      }))
                      : filteredOptions
                  }
                  onChange={(_key: string, value: any) => {
                    setApprover({
                      ...approver,
                      userId: value,
                    });
                  }}
                />

                {(approver.isEditing || isAllowedAddGateDecisionOwner) && (
                  <Box display="flex" alignItems="center" mt={1}>
                    <Checkbox
                      checked={approver.isGateDecision}
                      onChange={() => {
                        // If rule violated, set error message instead of toggling
                        if (
                          isGateDecisionExist &&
                          !(isEditApprover && approver.isGateDecision)
                        ) {
                          setValidationMessage(
                            "Only one Gate Decision Owner can be assigned.",
                          );
                          return;
                        }
                        setApprover({
                          ...approver,
                          isGateDecision: !approver.isGateDecision,
                        });
                        setValidationMessage(null); // clear message if valid
                      }}
                    />
                    <FormLabel>
                      Select if this person is gate decision maker
                    </FormLabel>
                  </Box>
                )}

                {validationMessage && (
                  <FormHelperText error>{validationMessage}</FormHelperText>
                )}

                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    onClick={() => {
                      setFormStatusApprover(false);
                      setIsEditApprover(false);
                      setApprover({
                        status: false,
                        userId: null,
                        isGateDecision: false,
                        isEditing: false,
                      });
                      setValidationMessage(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleApproverSubmit}
                    disabled={
                      isAddingApprover ||
                      isEditingApprover ||
                      isUpdatingApprover ||
                      !approver.userId
                    }
                    startIcon={
                      isAddingApprover ||
                        isEditingApprover ||
                        isUpdatingApprover ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : approver.isEditing ? (
                        <Edit />
                      ) : (
                        <Add />
                      )
                    }
                  >
                    {approver.isEditing ? "Update Approver" : "Save Approver"}
                  </Button>
                </Box>
              </Box>
              </DataLayout>
            </Box>
          )}
          <Box>
            {isFetchingApprover && approvalsPage === 1 ? (
              <Box
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
                gap={2}
                py={2}
              >
                <CircularProgress />
              </Box>
            ) : accumulatedApprovals?.length === 0 ? (
              <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
              >
                <Alert severity="warning">No approvers assigned</Alert>
              </Box>
            ) : (
              <>
                {accumulatedApprovals?.map((approval: any) => (
                  <Box
                    key={approval.id}
                    border="1px solid"
                    borderColor="divider"
                    borderRadius={1}
                    p={2}
                    mb={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="body2">
                        {approval.user_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {approval.role_name} -{" "}
                        {approval.is_decision_gate
                          ? "Gate Decision Maker"
                          : "Approver"}
                      </Typography>
                    </Box>
                    {isAllowedEditApprover && !isStageGateCompleted && approval.status !== "approved" && (
                      <Button
                        startIcon={<Edit />}
                        onClick={() => handleEditApprover(approval)}
                      />
                    )}
                  </Box>
                ))}
                {isFetchingApprover && approvalsPage > 1 && (
                  <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={24} />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      )}

      {/* CHECKLIST TAB */}
      {currentTab === 2 && (
        <Box ref={checklistsScrollRef} px={2} display={"flex"} flexDirection={"column"} gap={2} sx={{ overflowY: 'auto', flex: 1 }} onScroll={handleChecklistsScroll as any}>
          {!formStatusChecklist &&
            isAllowedAddChecklist &&
            !isStageGateCompleted && (
              <Button
                onClick={() => setFormStatusChecklist(true)}
                variant="outlined"
              >
                Create Checklist
              </Button>
            )}
          {formStatusChecklist && (
            <Box flexShrink={0}>
              <DataLayout title="Checklist" bgVariant="filled">
              <Box p={2} display={"flex"} flexDirection={"column"} gap={2}>
                <FieldControl
                  variant="input"
                  label="Checklist Title"
                  keyName="task_name"
                  value={checkList.task_name}
                  onChange={(_key: string, value: any) => {
                    setCheckList({ ...checkList, task_name: value });
                  }}
                />
                <FieldControl
                  keyName={"userId"}
                  label={"Approver"}
                  variant={"select"}
                  value={checkList.userId}
                  options={userOptions.map((u) => ({
                    value: u.id,
                    label: u.name,
                  }))}
                  onChange={(_key: string, value: any) => {
                    setCheckList({
                      ...checkList,
                      userId: value,
                    });
                  }}
                />
                <Divider />
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="text"
                    onClick={() => {
                      setFormStatusChecklist(false);
                      setCheckList({
                        task_name: null,
                        userId: null,
                        owner: null,
                        isEvidenceReq: false,
                        isEditing: false,
                      });
                    }}
                    disabled={isAddingChecklist || isEditingChecklist}
                  >
                    Cancel
                  </Button>
                  <Button
                    startIcon={
                      isAddingChecklist || isEditingChecklist ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : checkList.isEditing ? (
                        <Edit />
                      ) : (
                        <Add />
                      )
                    }
                    variant="contained"
                    onClick={handleChecklistSubmit}
                    disabled={isAddingChecklist || isEditingChecklist}
                  >
                    {checkList.isEditing ? "Update Item" : "Add Item"}
                  </Button>
                </Box>
              </Box>
              </DataLayout>
            </Box>
          )}
          <Box>
            {isFetchingCheckList && checklistsPage === 1 ? (
              <Box
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
                gap={2}
                py={2}
              >
                <CircularProgress />
              </Box>
            ) : accumulatedChecklists?.length === 0 ? (
              <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
              >
                <Alert severity="warning">No checklist assigned</Alert>
              </Box>
            ) : (
              <>
                {accumulatedChecklists?.map((item: any) => (
                  <Box
                    key={item.id}
                    border="1px solid"
                    borderColor="divider"
                    borderRadius={1}
                    p={2}
                    mb={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="body2">{item.task_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Owner : {item.owner}
                      </Typography>
                    </Box>
                    {isAllowedEditChecklist && !isStageGateCompleted && (
                      <Button
                        startIcon={<Edit />}
                        onClick={() => handleEditCheckList(item)}
                      />
                    )}
                  </Box>
                ))}
                {isFetchingCheckList && checklistsPage > 1 && (
                  <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={24} />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      )}

      {currentTab === 3 && (
        <Box height={"100%"} overflow={"hidden"} display={"flex"}>
          <ProjectArtefact
            projectId={projectId}
            isStageGateCompleted={isStageGateCompleted}
            stageGateId={stageGateId}
          />
        </Box>
      )}

      <Dialog open={!!currentWarning} onClose={handleWarningCancel}>
        <DialogTitle>Stage Gate Date Warning</DialogTitle>
        <DialogContent>
          <DialogContentText>{currentWarning}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarningCancel}>Cancel</Button>
          <Button onClick={handleWarningIgnore} autoFocus>
            {warningQueue.length > 1 && warningQueue.indexOf(currentWarning!) &&
              warningQueue.length - 1
              ? "Ignore & Continue"
              : "Ignore & Save"
            }
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
});

export const StageGateFormActions: React.FC<{
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  isEditing?: boolean;
  isDeleteAllowed?: boolean;
}> = ({ onSave, onCancel, onDelete, isEditing, isDeleteAllowed }) => (
  <>
    <Button
      variant="outlined"
      onClick={onCancel}
      sx={{ textTransform: "uppercase" }}
    >
      Cancel
    </Button>
    {isEditing && onDelete && isDeleteAllowed && (
      <Button
        variant="outlined"
        color="primary"
        onClick={onDelete}
        sx={{ textTransform: "uppercase" }}
      >
        Delete
      </Button>
    )}
    <Button
      variant="contained"
      onClick={onSave}
      sx={{ textTransform: "uppercase" }}
    >
      Save Details
    </Button>
  </>
);
